// redux/profiles/giftSlice.ts

import { PayloadAction } from "@reduxjs/toolkit";
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
        fetchSpiritualGiftsApi, fetchSurveyQuestionsApi,
        submitOneAnswerApi, fetchSurveyAnswersApi, 
        fetchSurveyProgressApi, submitSurveyAnswersApi,
        cancelSurveyApi
} from '../../services/profiles/memberProfileApi';

import { GiftSurveyState } from "../../types/profiles/giftTypes";


const initialState: GiftSurveyState = {
  questions: [],
  currentQuestion: 0,
  answers: {},
  loading: false,
  surveyStatus: 'idle',
  gifts: [],
  createdAt: null,
  language: 'en',
  progress: { current_question: 0, answered_questions: [] },

  error: null,
  successMessage: null,
};

// Thunk to fetch spiritual gifts for the member
export const fetchMemberSpiritualGifts = createAsyncThunk(
  'gift/fetchMemberSpiritualGifts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchSpiritualGiftsApi();
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || 'An error occurred while fetching spiritual gifts.');
    }
  }
);

// Thunk to submit survey answers and save results
export const submitSurveyAnswers = createAsyncThunk(
  'gift/submitSurveyAnswers',
  async (_, { rejectWithValue }) => {
    try {
      const data = await submitSurveyAnswersApi();
      return data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || 'An error occurred while submitting survey answers.');
    }
  }
);

// Thunk to fetch survey questions with dynamic language
export const fetchSurveyQuestions = createAsyncThunk(
  'gift/fetchSurveyQuestions',
  async (language: string, { rejectWithValue }) => {
    try {
      const data = await fetchSurveyQuestionsApi(language);
      return data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || 'An error occurred while fetching survey questions.');
    }
  }
);

// // Thunk to submit survey answers
export const submitOneAnswer = createAsyncThunk(
  'gift/submitOneAnswer',
  async (answer: { questionId: number; value: number; questionNumber: number }, { rejectWithValue }) => {
    try {
      const data = await submitOneAnswerApi(answer);
      return data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || 'An error occurred while submitting answer.');
    }
  }
);

// Thunk to fetch previous answers
export const fetchSurveyAnswers = createAsyncThunk(
  'gift/fetchSurveyAnswers',
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchSurveyAnswersApi();
      return data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || 'An error occurred while fetching answers.');
    }
  }
);

// Thunk to fetch survey progress (the last answered question and the progress)
export const fetchSurveyProgress = createAsyncThunk(
  'gift/fetchSurveyProgress',
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchSurveyProgressApi();
      return data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || 'An error occurred while fetching survey progress.');
    }
  }
);

// Thunk for Cancel survey
export const cancelSurvey = createAsyncThunk(
  'gift/cancelSurvey',
  async (_, { rejectWithValue }) => {
    try {
      const response = await cancelSurveyApi();
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || 'An error occurred while canceling the survey.');
    }
  }
);

const giftSlice = createSlice({
  name: 'gift',
  initialState,
  reducers: {
    setCurrentQuestion: (state, action: PayloadAction<number>) => {
      state.currentQuestion = action.payload;
    },
    setAnswer: (state, action: PayloadAction<{ questionId: number; answer: number }>) => {
      state.answers[action.payload.questionId] = action.payload.answer;
    },
    setLanguage: (state, action: PayloadAction<string>) => {
      state.language = action.payload;
    },
    resetSurvey: (state) => {
      state.currentQuestion = 0;
      state.surveyStatus = 'idle';
      state.answers = {};
      state.progress = { current_question: 0, answered_questions: [] };
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    
  },
  extraReducers: (builder) => {
    builder

      // Member spritual gifts
      .addCase(fetchMemberSpiritualGifts.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.gifts = [];
        state.createdAt = null;
      })
      .addCase(fetchMemberSpiritualGifts.fulfilled, (state, action) => {
        state.loading = false;
        state.gifts = action.payload.gifts || [];
        state.createdAt = action.payload.created_at || null;      
        if (!action.payload.gifts?.length) {
          state.gifts = [];
          state.createdAt = null;
        }
      })
      .addCase(fetchMemberSpiritualGifts.rejected, (state, action) => {
        state.loading = false;
        state.gifts = [];
        state.error = action.payload as string;
      })

      // Submit survey answers
      .addCase(submitSurveyAnswers.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null; 
      })
      .addCase(submitSurveyAnswers.fulfilled, (state, action) => {
        state.loading = false;
        state.gifts = action.payload.gifts || [];
        state.surveyStatus = 'completed';
        state.successMessage = action.payload.message || "Survey submitted successfully.";
      })
      .addCase(submitSurveyAnswers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.surveyStatus = 'error';
      })

      // Fetch survey questions
      .addCase(fetchSurveyQuestions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSurveyQuestions.fulfilled, (state, action) => {
        state.loading = false;
        state.questions = action.payload;

        
      })
      .addCase(fetchSurveyQuestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Submit one answer
      .addCase(submitOneAnswer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitOneAnswer.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(submitOneAnswer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch survey answers
      .addCase(fetchSurveyAnswers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSurveyAnswers.fulfilled, (state, action) => {
        state.loading = false;
        const answers = action.payload.reduce((acc: Record<number, number>, item: any) => {
          acc[item.question.question_number] = item.answer;
          return acc;
        }, {});
        state.answers = answers;
      })
      .addCase(fetchSurveyAnswers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch survey progress
      .addCase(fetchSurveyProgress.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(fetchSurveyProgress.fulfilled, (state, action) => {
        state.loading = false;
        state.progress = action.payload;
        state.currentQuestion =
          action.payload.current_question > 0 ? action.payload.current_question - 1 : 0;
      })
      .addCase(fetchSurveyProgress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Cancel survey
      .addCase(cancelSurvey.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelSurvey.fulfilled, (state) => {
        state.loading = false;
        state.answers = {};
        state.progress = { current_question: 0, answered_questions: [] };
        state.surveyStatus = 'idle';
        state.currentQuestion = state.questions.length > 0 ? 0 : -1;
        state.successMessage = "Survey canceled successfully.";
      })
      .addCase(cancelSurvey.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setCurrentQuestion, setAnswer, resetSurvey, setLanguage, clearSuccessMessage } = giftSlice.actions;
export default giftSlice.reducer;

